import { lightTint } from 'src/secret-menu/brand-asset-builder/lib/hexColour';
import type { Company } from 'src/secret-menu/hooks/useCompanies';

export type BrandAssetBuilderPalette = {
  primary: string;
  secondary: string;
  accent: string;
  darkText: string;
  background: string;
};

export type BrandAssetBuilderTitlePageTheme =
  | 'layered-wave'
  | 'soft-horizon'
  | 'diagonal-sweep'
  | 'stepped-horizon'
  | 'corner-swell'
  | 'opposing-ribbons'
  | 'corner-cut'
  | 'crescent-arc';

export type BrandAssetBuilderShapeColour = 'primary' | 'secondary' | 'accent' | 'darkText';

// 'banded' maps to the `AfternoonTable` master-template style (tinted, alternating row shading),
// 'grid' to Word's built-in `TableGrid` (plain uniform borders, no shading) - see docxModifier.ts.
export type BrandAssetBuilderTableStyle = 'banded' | 'grid';

// Mirrors the backend's `BulletCharacter` enum (ExportBrandingConfig.py) - the auto-brand and
// "generate and deploy" flows parse the pack's bullet glyphs through it and drop anything unknown.
export type BrandAssetBuilderBulletStyle = 'disc' | 'square' | 'circle' | 'diamond' | 'dash';

export type BrandAssetBuilderLogoPosition = 'left' | 'center' | 'right';

export type BrandAssetBuilderTitlePageConfig = {
  shapeColor: BrandAssetBuilderShapeColour;
  logoScale: number;
  logoPosition: BrandAssetBuilderLogoPosition;
  logoX: number;
  logoY: number;
  hideDate: boolean;
};

export type BrandAssetBuilderData = {
  company: Company | null;
  clientName: string;
  logoFile: File | null;
  logoUrl: string | null;
  palette: BrandAssetBuilderPalette;
  extractedColours: string[];
  tableStyle: BrandAssetBuilderTableStyle;
  tableBorderColour: string;
  tableHeaderColour: string;
  tableHeaderLighten: boolean;
  bulletColour: string;
  bulletStyle: BrandAssetBuilderBulletStyle;
  headingFont: string;
  bodyFont: string;
  titlePageTheme: BrandAssetBuilderTitlePageTheme;
  titlePageConfig: BrandAssetBuilderTitlePageConfig;
};

export const DEFAULT_BRAND_ASSET_BUILDER_PALETTE: BrandAssetBuilderPalette = {
  primary: '#E7E7E7',
  secondary: '#878787',
  accent: '#4B4B4B',
  darkText: '#1A1A1A',
  background: '#FFFFFF',
};

export const initialBrandAssetBuilderData: BrandAssetBuilderData = {
  company: null,
  clientName: '',
  logoFile: null,
  logoUrl: null,
  palette: DEFAULT_BRAND_ASSET_BUILDER_PALETTE,
  extractedColours: [],
  tableStyle: 'banded',
  tableBorderColour: lightTint(DEFAULT_BRAND_ASSET_BUILDER_PALETTE.primary),
  tableHeaderColour: DEFAULT_BRAND_ASSET_BUILDER_PALETTE.primary,
  tableHeaderLighten: false,
  bulletColour: DEFAULT_BRAND_ASSET_BUILDER_PALETTE.accent,
  bulletStyle: 'disc',
  headingFont: 'Calibri',
  bodyFont: 'Calibri',
  titlePageTheme: 'layered-wave',
  titlePageConfig: {
    shapeColor: 'primary',
    logoScale: 100,
    logoPosition: 'center',
    logoX: 50,
    logoY: 15,
    hideDate: false,
  },
};
